import { ChangeEvent, FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { TableBody as Body, SelectChangeEvent } from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';

import { Badge, CustomAccordion, CustomSelect, CustomSwitch, TableNoData, Text } from '../../shared/uiComponents';
import { BadgeTypes, ClientStatusTypes, colors } from '../../shared/uiComponents/StatusBadge';
import { CustomTableRow, RowTableCell } from '../../shared/uiComponents/Table/tableHelpers';
import { HeaderProperties, TableDataProperties } from '../../shared/uiComponents/Table/tableProperties';
import { SelectDataProperties } from '../../shared/uiComponents/Dropdown';
import CustomDrawer from '../../shared/uiComponents/Drawer';
import { InfoRow as Row } from '../../components/StyledComponents';
import { AdminTypes } from '../../components/Menu';
import { ClientInfo } from './clientInfo';

import { DispatchProperties, useSelector } from '../../redux/store';
import { InfoClientProperties } from '../../redux/API/ClientAPIHelpers/clientProperties';
import { getCaseCoordinators, getTherapists } from '../../redux/State/clientSlice/userClientSlice';
import { clearSkillAreas } from '../../redux/State/clientSlice/skillAreaSlice';
import { getBranchBadgeType } from '../../shared/Helpers/constants';
import { getFunders } from '../../redux/State/clientSlice/fundingSlice';
import { formatClientName } from '../../shared/Helpers/functions';

export interface RowRendererProperties {
	data: InfoClientProperties[];
}

export interface ClientsDataProperties extends TableDataProperties {
	query: InfoClientProperties[] | null;
}

export const Headers: HeaderProperties[] = [
	{ id: '6', name: 'Full Name', orderBy: 'fullName' },
	{ id: '1', name: 'Email', orderBy: 'email' },
	{ id: '3', name: 'Parent Phone', orderBy: 'parentPhoneNumber' },
	{ id: '4', name: 'Status', orderBy: 'status.id' },
	{ id: '5', name: 'Branch', width: '120px' },
	{ id: '6', name: '', width: '40px' },
];

export const ClientDetailedInfo: FC<InfoClientProperties> = (info) => {
	const dispatch = useDispatch<DispatchProperties>();

	const therapists = useSelector((state) => state.userClient.therapists);
	const defaultBCBA = useSelector((state) => state.userClient.defaultBCBA);
	const role = useSelector((state) => state.account.role);
	const isBT = role?.section.id === AdminTypes.bt;

	useEffect(() => {
		if (!info.id) return;
		dispatch(getTherapists({ clientId: info.id }));
	}, [info, dispatch]);

	return (
		<>
			<Row>
				<Text title="First Name: " type="psm" fontWeight="semiBold" />
				<Text title={formatClientName(info.firstName, info.isElc)} type="psm" />
			</Row>
			<Row>
				<Text title="Last Name: " type="psm" fontWeight="semiBold" />
				<Text title={formatClientName(info.lastName, info.isElc)} type="psm" />
			</Row>
			<Row>
				<Text title="Nickame: " type="psm" fontWeight="semiBold" />
				<Text title={info.nickName ? info.nickName : '-'} type="psm" />
			</Row>
			<Row>
				<Text title="Email: " type="psm" fontWeight="semiBold" />
				<Text title={info.email} type="psm" />
			</Row>
			<Row>
				<Text title="Secondary Email: " type="psm" fontWeight="semiBold" />
				<Text title={info.secondaryEmail ? info.secondaryEmail : '-'} type="psm" />
			</Row>
			<Row>
				<Text title="Parent Phone Number: " type="psm" fontWeight="semiBold" />
				<Text title={info.parentPhoneNumber} type="psm" />
			</Row>
			<Row>
				<Text title="Secondary Phone Number: " type="psm" fontWeight="semiBold" />
				<Text title={info.secondaryNumber ? info.secondaryNumber : '-'} type="psm" />
			</Row>
			<Row>
				<Text title="DOB: " type="psm" fontWeight="semiBold" />
				<Text title={dayjs(info.dateOfBirthday).format('MM-DD-YYYY')} type="psm" />
			</Row>
			<Row>
				<Text title="Gender: " type="psm" fontWeight="semiBold" />
				<Text title={info.gender} type="psm" />
			</Row>
			<Row>
				<Text title="Address: " type="psm" fontWeight="semiBold" />
				<Text title={info.address} type="psm" />
			</Row>
			{Boolean(info.caseCoordinator) && (
				<Row>
					<Text title="Case Manager: " type="psm" fontWeight="semiBold" />
					<Text title={info.caseCoordinator.fullName} type="psm" />
				</Row>
			)}
			{!isBT &&
				Boolean(info.insurances?.length) &&
				info.insurances.map((insurance) => (
					<>
						<Row>
							<Text
								title={`${insurance.type === 1 ? 'Primary' : 'Secondary'} Insurance:`}
								type="psm"
								fontWeight="semiBold"
							/>
							<Text title={insurance.name as string} type="psm" />
						</Row>
						<Row>
							<Text
								title={`${insurance.type === 1 ? 'Primary' : 'Secondary'} Member ID:`}
								type="psm"
								fontWeight="semiBold"
							/>
							<Text title={insurance.insuranceNumber} type="psm" />
						</Row>
					</>
				))}
			{!isBT && (
				<Row>
					<Text title="MBH ID: " type="psm" fontWeight="semiBold" />
					<Text title={info.mbhId} type="psm" />
				</Row>
			)}
			{!isBT && info.lastMondaySyncedAt && (
				<Row>
					<Text title="Synced from Monday: " type="psm" fontWeight="semiBold" />
					<Text title={dayjs(info.lastMondaySyncedAt).format('MM/DD/YYYY hh:mm A')} type="psm" />
				</Row>
			)}
			{!isBT && (
				<CustomAccordion
					headerStyle={{ padding: 0 }}
					HeaderContent={() => (
						<Row>
							<Text title="Number of Providers: " type="psm" fontWeight="semiBold" />
							<Text title={`${info.numberOfTherapist}`} type="psm" />
						</Row>
					)}
					Content={() =>
						therapists?.length ? (
							<>
								{therapists?.map((therapist) => (
									<Text
										key={`therapist-${therapist.id}`}
										title={`${therapist.fullName} (${therapist.role.section.name}) ${
											defaultBCBA?.id === therapist.id ? '(Supervisor)' : ''
										}`}
										className="marginRight8"
										type="psm"
									/>
								))}
							</>
						) : (
							<Text title="None" type="psm" />
						)
					}
				/>
			)}
		</>
	);
};

export const TableBody: FC<RowRendererProperties> = ({ data }) => {
	const dispatch = useDispatch<DispatchProperties>();

	const navigateClientHandler = () => {
		dispatch(clearSkillAreas());
	};

	if (!data?.length) {
		return <TableNoData spanScope={Headers.length} />;
	}

	return (
		<Body>
			{data.map((row) => (
				<CustomTableRow key={`row-${row.id}`}>
					<RowTableCell>
						<Link to={`/clients/client/${row.id}`} onClick={navigateClientHandler}>
							{formatClientName(row.fullName, row.isElc)}
						</Link>
					</RowTableCell>
					<RowTableCell>{row.email}</RowTableCell>
					<RowTableCell>{row.parentPhoneNumber ? row.parentPhoneNumber : '-'}</RowTableCell>
					<RowTableCell>
						<Badge label={row.status.name} sizeType="md" type={ClientStatusTypes[row.status.id] as BadgeTypes} />
					</RowTableCell>
					<RowTableCell>
						<div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
							{row.branches && row.branches.length > 0 ? (
								row.branches.map((branch) => (
									<Badge key={branch.id} label={branch.name} type={getBranchBadgeType(branch.state)} sizeType="sm" />
								))
							) : (
								<span style={{ color: '#999', fontSize: '12px' }}>None</span>
							)}
						</div>
					</RowTableCell>
					<RowTableCell>
						<ClientInfo item={row} />
					</RowTableCell>
				</CustomTableRow>
			))}
		</Body>
	);
};

const labels = ['Pending', 'Active', 'Paused', 'Discharged', 'All'];

const directionData: SelectDataProperties[] = [
	{ id: 'asc', name: 'Ascending' },
	{ id: 'desc', name: 'Descending' },
];

export const clientSortData: SelectDataProperties[] = [
	{ id: '', name: 'none' },
	{ id: 'fullName', name: 'Full Name' },
	{ id: 'numberOfTherapist', name: 'Providers' },
	{ id: 'parentPhoneNumber', name: 'Parent Phone Number' },
	{ id: 'status', name: 'Status' },
];

const FiltersContent = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params, setParams] = useSearchParams();

	const caseCoordinators = useSelector((state) => state.userClient.caseCoordinators);
	const role = useSelector((state) => state.account.role);
	const funders = useSelector((state) => state.funding.funders);

	const isAdmin = role?.section.id === AdminTypes.admin;
	const data = caseCoordinators ? caseCoordinators.map((x) => ({ id: x.id, name: x.fullName })) : [];
	const caseCoordinatorId = params.get('caseCoordinatorId') ?? '';
	const statusType = params.get('status') ?? '';
	const orderBy = params.get('orderBy') ?? '';
	const direction = params.get('direction') ?? 'asc';
	const funderFilterId = params.get('funderId') ?? '';
	const hasActiveFunderAuth = params.get('hasActiveFunderAuthorization') === 'true';

	const funderFilterOptions = [
		{ id: '', name: 'All (no filter)' },
		...funders.map((f) => ({ id: f.id, name: f.name })),
	];

	const closeHandler = () => {
		params.set('filter', 'close');
		setParams(params, { replace: true });
	};

	const selectStatusHandler = (name: string, id: number) => {
		params.set('page', '1');

		if (name.toLowerCase() === 'all') {
			params.delete('status');
			setParams(params, { replace: true });
			closeHandler();

			return;
		}
		params.set('status', `${id}`);
		setParams(params, { replace: true });
		closeHandler();
	};

	const onCaseCoordinatorChange = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		params.set('caseCoordinatorId', value);
		setParams(params, { replace: true });
		closeHandler();
	};

	const handleOrderBy = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;
		params.set('orderBy', value);
		params.set('page', '1');
		setParams(params, { replace: true });
		closeHandler();
	};

	const handleDirection = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;
		params.set('direction', value);
		params.set('page', '1');
		setParams(params, { replace: true });
		closeHandler();
	};

	const onFunderChange = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		if (value) {
			params.set('funderId', value);
		} else {
			params.delete('funderId');
		}
		params.set('page', '1');
		setParams(params, { replace: true });
		closeHandler();
	};

	const onHasActiveFunderAuthChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			params.set('hasActiveFunderAuthorization', 'true');
		} else {
			params.delete('hasActiveFunderAuthorization');
		}
		params.set('page', '1');
		setParams(params, { replace: true });
		closeHandler();
	};

	useEffect(() => {
		dispatch(getCaseCoordinators());
		dispatch(getFunders());
	}, [dispatch]);

	return (
		<>
			<Text title="Select client status: " type="h6" font="header" fontWeight="medium" />
			<div className="clientFiltersStatusBadges">
				{Boolean(labels) &&
					labels.map((label, index) => {
						const type = ClientStatusTypes[index] as BadgeTypes;
						const color = colors[type];

						return (
							<div
								key={index}
								className="clientFiltersStatusBadge"
								style={{
									outline: parseInt(statusType) === index ? `1px solid ${color}` : 'none',
								}}
							>
								<Badge label={label} sizeType="lg" type={type} onClick={() => selectStatusHandler(label, index)} />
							</div>
						);
					})}
			</div>
			<div className="clientFiltersMobileSort">
				<CustomSelect
					label="Select sort"
					data={clientSortData}
					value={orderBy}
					setValue={handleOrderBy}
					className="marginBottom16"
				/>
				<CustomSelect
					label="Select direction"
					data={directionData}
					value={direction}
					setValue={handleDirection}
					className="marginBottom16"
				/>
			</div>
			{isAdmin && (
				<CustomSelect
					label="Select case manager: "
					setValue={onCaseCoordinatorChange}
					value={caseCoordinatorId}
					data={[{ id: '', name: 'Clear' }, ...data]}
				/>
			)}
			{isAdmin && (
				<>
					<CustomSelect
						label="Funder"
						setValue={onFunderChange}
						value={funderFilterId}
						data={funderFilterOptions}
						className="marginBottom16"
					/>
					<CustomSwitch
						label="Has active funder authorization"
						checked={hasActiveFunderAuth}
						onChange={onHasActiveFunderAuthChange}
					/>
				</>
			)}
		</>
	);
};

export const ClientFilters = () => {
	const [params, setParams] = useSearchParams();

	const open = params.get('filter') === 'open';

	const closeHandler = () => {
		params.set('filter', 'close');
		setParams(params, { replace: true });
	};

	return <CustomDrawer label="Filters" open={open} onClose={closeHandler} isSecondary Content={FiltersContent} />;
};
